import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const SecurityCard = () => {
  const { t } = useLang();
  const [hasPassword, setHasPassword] = useState(null);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/auth/password/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { has_password: false }))
      .then((d) => setHasPassword(!!d.has_password))
      .catch(() => setHasPassword(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error(t("sec_min_len"));
    if (next !== confirm) return toast.error(t("sec_mismatch"));
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/password/set`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ new_password: next, current_password: hasPassword ? current : null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : t("sec_fail"));
      toast.success(t("sec_saved"));
      setHasPassword(true);
      setOpen(false);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "field-input field-input-dark w-full";

  return (
    <section className="mb-14 rounded-3xl bg-[#132338] border border-[#203859] p-6" data-testid="security-card">
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F26522]/15 text-[#F26522]">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-lg font-bold text-white">{t("sec_title")}</h2>
            {hasPassword !== null && (
              <span
                className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] font-bold ${
                  hasPassword ? "bg-[#25D366]/15 text-[#25D366]" : "bg-amber-500/15 text-amber-400"
                }`}
                data-testid="security-status"
              >
                {hasPassword ? t("sec_status_set") : t("sec_status_unset")}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-400">{hasPassword ? t("sec_sub_set") : t("sec_sub_unset")}</p>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F26522]/60 text-[#F26522] hover:bg-[#F26522] hover:text-white font-display font-bold px-5 py-3 text-sm transition-colors duration-200 whitespace-nowrap"
            data-testid="security-open-btn"
          >
            <KeyRound className="h-4 w-4" /> {hasPassword ? t("sec_change_btn") : t("sec_create_btn")}
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={save} className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="security-form">
          {hasPassword && (
            <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder={t("sec_current_ph")} className={input} data-testid="security-current-input" />
          )}
          <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={t("sec_new_ph")} className={input} data-testid="security-new-input" />
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("sec_confirm_ph")} className={input} data-testid="security-confirm-input" />
          <div className="md:col-span-3 flex flex-wrap gap-3">
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-6 py-3 text-sm transition-colors duration-200 disabled:opacity-60" data-testid="security-save-btn">
              {busy ? t("pw_sending") : t("sec_save_btn")}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-white/20 text-slate-300 hover:text-white px-6 py-3 text-sm font-semibold transition-colors duration-200" data-testid="security-cancel-btn">
              {t("sec_cancel")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};
